import { useState } from 'react'

import { useAuthContext } from '@contexts/authContext'
import { format, formatDistance } from 'date-fns'
import { useLongPress } from 'use-long-press'
import { colors, size } from '@styles/theme'
import vibrate from '@utils/vibrate'
import styled from '@emotion/styled'

import MessageReactionList from './MessageReactionList'
import MessageActions from './MessageActions'
import { PiTimerBold } from 'react-icons/pi'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  overflow-anchor: none;
`;

const MessageBubble = styled.div`
  position: relative;
  white-space: pre-wrap;
  flex-shrink: 1;
  // padding: 0.55rem 1rem;
  padding: 0.65rem 1rem;
  // padding: 0.65rem 0.85rem;
  // color: rgb(235, 235, 235);
  // color: white;
  color: rgba(255,255,255,0.9);
  // line-height: 20px;
  line-height: 22.75px;
  // line-height: 23px;
  font-size: 15.85px;
  // font-size: 16px;
  font-weight: 400;
  & span {
    letter-spacing: 0.4px;
    word-spacing: -0.75px;
  }
`;

const MetaData = styled.div`
  font-size: 11px;
  height: ${size(2.5)};
`;

// function shrinkWrap(element) {
//   const { firstChild, lastChild } = element
//   if (!element || !firstChild || !lastChild) return
//   const range = document.createRange()
//   range.setStartBefore(firstChild)
//   range.setEndAfter(lastChild)
//   const { width } = range.getBoundingClientRect()
//   element.style.width = width + 1 + 'px'
//   element.style.boxSizing = 'content-box'
// }

function isEmojiOnly(str) {
  // remove all white spaces from the input
  const stringToTest = str.replace(/ /g, '')
  const regexForEmojis = /\p{Extended_Pictographic}/gu
  const regexForAlphaNums = /[\p{L}\p{N}]+/gu

  // check to see if the string contains emojis
  if (regexForEmojis.test(stringToTest)) {
    // check to see if it contains any alphanumerics
    if (regexForAlphaNums.test(stringToTest)) {
      return false
    }
    return true
  }

  return false
}

const OtherUserImage = styled.img`
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: flex-end;
  flex-shrink: 0;

  height: ${size(3.25)};
  width: ${size(3.25)};
  border-radius: 100px;
  margin-right: ${size(0.5)};

  background-color: grey;
`;

const OtherUser = ({
  userThumbnail,
  nextIsSame,
  nextIsMoreThanFiveMinsLater,
  isAvatar,
}) => (
  <OtherUserImage
    src={userThumbnail}
    style={{
      visibility:
        !nextIsSame || (nextIsSame && nextIsMoreThanFiveMinsLater)
          ? 'visible'
          : 'hidden',
      filter: isAvatar && 'grayscale(100%) contrast(120%)',
    }}
  />
);

const ImageInMessage = ({ image, imageBorderRadius, bindLongPress }) => {
  return (
    <div
      {...bindLongPress()}
      style={{
        position: 'relative',
        maxWidth: '75svw',
        height: `calc(${image.height}/${image.width}*75svw)`,
        maxHeight: '65svh',
      }}
    >
      {/* NOTE: The animation image was working very inconsistently... */}
      {/* <AnimationImage */}
      <img
        src={image?.url}
        style={{
          flexShrink: 0,
          width: '75svw',
          height: `calc(${image.height}/${image.width}*75svw)`,
          maxHeight: '65svh',
          borderRadius: '25px',
          objectPosition: 'center',
          objectFit: 'cover',
          borderRadius: imageBorderRadius(),
          // border: '0.5px solid rgba(255,255,255, 0.25)',
        }}
      />
    </div>
  )
}

const Message = ({
  message,
  nextMessage,
  prevMessage,
  openMetaData,
  closeMetaData,
  destroyMessage,
  createMessageReaction,
  destroyMessageReaction,
  newMessageAt,
  userThumbnail,
  isAvatar,
}) => {
  const { state: authState } = useAuthContext()
  const [actionsOpen, setActionsOpen] = useState(false)
  const [bubbleRef, setBubbleRef] = useState(null)
  // used for showing message actions panel when the message bubble is pressed
  const bindLongPress = useLongPress(openActions)

  // Short circuit rendering if the message is empty
  if (
    (message?.media?.length === 0) &&
    !message?.body &&
    !message?.fakeId
  ) return

  // opens the message actions 
  function openActions() {
    vibrate()
    setActionsOpen(true)
  }

  // Whether the message belongs to the current user
  const isOwnMessage = message?.user?.uuid === authState?.user?.uuid

  // Detect if the message is all emojis
  const trimmedBody = message?.body?.trim()
  const isEmojiString = trimmedBody?.length > 0 && isEmojiOnly(trimmedBody)
  const isMediaOnly = !message?.body && Array.isArray(message?.media) && message?.media?.length > 0

  // Whether the next message has the same ownership as the current
  const nextIsOwnMessage =
    nextMessage && nextMessage?.user?.uuid === authState?.user?.uuid
  const nextIsSame = nextMessage && isOwnMessage === nextIsOwnMessage

  // Whether the previous message hasthe same ownership as the current
  const prevIsOwnMessage =
    prevMessage && prevMessage?.user?.uuid === authState?.user?.uuid
  const prevIsSame = prevMessage && isOwnMessage === prevIsOwnMessage

  // Time related rendering logic
  //  - a timestamp gets inserted if theres more than a 1 hour gap
  //  - extra spacing is inserted if the messages are from the same person, but more than 5 mins apart
  const currentCreatedAt = new Date(
    message?.createdAt || message?.fakeCreatedAt
  )
  const prevCreatedAt = new Date(
    prevMessage?.createdAt || prevMessage?.fakeCreatedAt
  )
  const nextCreatedAt = new Date(
    nextMessage?.createdAt || nextMessage?.fakeCreatedAt
  )
  // Is the time gap greater than 1 hour?
  const prevIsMoreThanHourAgo =
    prevMessage && currentCreatedAt - prevCreatedAt > 3600000
  // Is the time gap greater than 5 minutes?
  const prevIsMoreThanFiveMinsAgo =
    prevMessage && currentCreatedAt - prevCreatedAt > 60000 * 5
  // Is the time gap greater than 5 minutes?
  const nextIsMoreThanFiveMinsLater =
    nextMessage && nextCreatedAt - currentCreatedAt > 60000 * 5

  const renderDate = (date) => {
    try {
      let time = formatDistance(date, Date.now());
      time = time.replace('about ', '')
      if (time === 'less than a minute') {
        return 'now'
      } else if (time.slice(time.length - 6) === 'minute') {
        return time.replace('minute', 'min')
      } else if (time.slice(time.length - 7) === 'minutes') {
        return time.replace('minutes', 'mins')
      } else if (time.slice(time.length - 4) === 'hour') {
        return time.replace('hour', 'hr')
      } else if (time.slice(time.length - 5) === 'hours') {
        return time.replace('hours', 'hrs')
      } else {
        return time
      }
    } catch {
      return ''
    }
  }

  // Toggles meta data opening and closing
  const handleMetaDataClick = () => {
    if (actionsOpen) return
    if (message?.isMetaDataOpen) {
      closeMetaData(message)
    } else {
      openMetaData(message)
    }
  }

  const renderMetaData = () => {
    if (message?.createdAt) {
      return renderDate(message?.createdAt)
    } else if (
      message?.fakeCreatedAt &&
      new Date() - message?.fakeCreatedAt > 1000
    ) {
      return (
        <PiTimerBold
          fill={
            isOwnMessage ? colors.statusColors.right_now('light') : 'lightgrey'
          }
          size={size(1.5)}
        />
      )
    }
  }

  const renderLargeTimeGap = () => {
    const hour = 3600000
    const day = hour * 24
    const week = day * 7
    const zeroHourToday = new Date(new Date().toDateString())
    // const zeroHourYesterday = zeroHourToday - hour24
    // const zeroHourWeekAgo = zeroHourToday - hour24 * 7
    const today = new Date()
    const createdAt = new Date(message?.createdAt || message?.fakeCreatedAt)
    if (today - createdAt < today - zeroHourToday) {
      // If today, just time ()
      return format(createdAt, 'kk:mm')
    } else if (today - createdAt < today - (zeroHourToday - day)) {
      // if yesterday, just yesterday (Yesterday * 11:42)
      return 'Yesterday ' + format(createdAt, 'kk:mm')
    } else if (today - createdAt < day * 7) {
      // Day of week, within a week (Wednesday * 12:49)
      return format(createdAt, 'EEEE kk:mm')
    } else {
      // Day of week and date (Monday, Feb 19 * 13:32)
      return format(createdAt, 'EEEE, MMM d - kk:mm')
    }
  }

  const messageBorderRadius = () =>
    isOwnMessage
      ? `25px ${prevIsSame && !prevIsMoreThanFiveMinsAgo ? 3 : 25}px ${
          (nextIsSame && !nextIsMoreThanFiveMinsLater) ||
          (Array.isArray(message?.media) && message?.media?.length > 0)
            ? 3
            : 25
        }px 25px`
      : `${prevIsSame && !prevIsMoreThanFiveMinsAgo ? 3 : 25}px 25px 25px ${
          (nextIsSame && !nextIsMoreThanFiveMinsLater) ||
          (Array.isArray(message?.media) && message?.media?.length > 0)
            ? 3
            : 25
        }px`

  const imageBorderRadius = () =>
    isOwnMessage
      ? `25px ${
          (prevIsSame && !prevIsMoreThanFiveMinsAgo) ||
          message?.body?.length > 0
            ? 3
            : 25
        }px ${nextIsSame && !nextIsMoreThanFiveMinsLater ? 3 : 25}px 25px`
      : `${
          (prevIsSame && !prevIsMoreThanFiveMinsAgo) ||
          message?.body?.length > 0
            ? 3
            : 25
        }px 25px 25px ${nextIsSame && !nextIsMoreThanFiveMinsLater ? 3 : 25}px`

  const renderDeletedBubble = (
    text = 'message deleted',
    borderType = 'message'
  ) => (
    <div style={{ display: 'flex' }}>
      {!isOwnMessage && (
        <OtherUser
          userThumbnail={userThumbnail || message?.user?.avatarUrls?.blue_medium}
          nextIsSame={nextIsSame}
          nextIsMoreThanFiveMinsLater={nextIsMoreThanFiveMinsLater}
          isAvatar={isAvatar}
        />
      )}
      <MessageBubble
        style={{
          // opacity: 0,
          // animation: 'fadeIn 200ms ease-in-out',
          // animationFillMode: 'forwards',
          backgroundColor: 'grey',
          border: '0.5px solid rgba(255,255,255,0.2)',
          borderRadius:
            borderType === 'message'
              ? messageBorderRadius()
              : imageBorderRadius(),
        }}
      >
        <i>{text}</i>
      </MessageBubble>
    </div>
  )

  const renderImages = () =>
    message?.media?.map?.((image, index) => {
      return (
        <ImageInMessage
          image={image}
          imageBorderRadius={imageBorderRadius}
          bindLongPress={bindLongPress}
        />
      )
    })

  return (
    <>
      <Container
        onClick={handleMetaDataClick}
        style={{
          alignItems: isOwnMessage ? 'flex-end' : 'flex-start',
          padding: isOwnMessage
            ? // ? `${prevIsSame ? 0.15 : 0}rem 0.3rem ${(nextIsSame && !nextIsMoreThanFiveMinsLater) ? 0 : 0.85}rem 3rem`
              // : `${prevIsSame ? 0.15 : 0}rem 3rem ${(nextIsSame && !nextIsMoreThanFiveMinsLater) ? 0 : 0.85}rem 0.3rem`,
              `${prevIsSame ? 0.15 : 0}rem 0.3rem ${
                nextIsSame && !nextIsMoreThanFiveMinsLater ? 0 : 0.85
              }rem 4.5rem`
            : `${prevIsSame ? 0.15 : 0}rem 4.5rem ${
                nextIsSame && !nextIsMoreThanFiveMinsLater ? 0 : 0.85
              }rem 0.3rem`,
        }}
      >
        {message?.isDestroyed && renderDeletedBubble()}
        {!message?.isDestroyed && (
          <div style={{ display: 'flex' }}>
            {!isOwnMessage && (
              <OtherUser
                userThumbnail={
                  userThumbnail || message?.user?.avatarUrls?.blue_medium
                }
                nextIsSame={nextIsSame}
                nextIsMoreThanFiveMinsLater={nextIsMoreThanFiveMinsLater}
                isAvatar={isAvatar}
              />
            )}
            <MessageBubble
              ref={(node) => node && setBubbleRef(node)}
              {...bindLongPress()}
              style={{
                // opacity: 0,
                // animation: 'fadeIn 200ms ease-in-out',
                // animationFillMode: 'forwards',
                backgroundColor: (isEmojiString || isMediaOnly)
                  ? 'transparent'
                  : isOwnMessage
                  ? '#7246cf'
                  : newMessageAt && new Date(message?.createdAt) >= newMessageAt
                  ? 'grey'
                  // : '#22212A',
                  // : '#212029',
                  // : '#31303d',
                  : '#333333', 
                borderRadius: messageBorderRadius(),
                fontSize: isEmojiString && '3.75rem',
                lineHeight: isEmojiString && '3.75rem',
                textAlign: isEmojiString && isOwnMessage ? 'right' : 'auto',
                padding: isMediaOnly ? 0 : isEmojiString && `${size(0.75)} 0`,
                textWrapStyle: 'pretty',
                // TODO: We need pre-text to calculate the widths properly so these
                //       don't render with excess whitespace
                // maxWidth: '70dvw',
              }}
            >
              {!message?.isDestroyed && isMediaOnly && renderImages()}
              <span {...bindLongPress()}>{message?.body}</span>
              {!message?.isDestroyed &&
                message?.messageReactions?.length > 0 && (
                  <MessageReactionList
                    messageReactions={message?.messageReactions}
                    bubbleRef={bubbleRef}
                    isOwnMessage={isOwnMessage}
                  />
                )}
            </MessageBubble>
          </div>
        )}
        {!message?.isDestroyed && message?.messageReactions?.length > 0 && (
          // Spacing for the reactions
          <div
            style={{ position: 'relative', width: '100%', height: size(1.5) }}
          />
        )}
        {!message?.isDestroyed && message?.isMetaDataOpen && (
          <MetaData
            style={{
              color: isOwnMessage
                ? colors.statusColors.right_now('light')
                : 'lightgrey',
              padding: isOwnMessage
                ? `${size(0.5)} ${size(1)} 0 0`
                : `${size(0.5)} 0 0 ${size(5.25)}`,
            }}
          >
            {renderMetaData()}
          </MetaData>
        )}
        <MessageActions
          open={actionsOpen}
          setOpen={setActionsOpen}
          anchorEl={bubbleRef}
          isOwnMessage={isOwnMessage}
          destroyFunc={destroyMessage}
          data={message}
          messageReactions={message?.messageReactions}
          clipboardContents={message?.body}
          createMessageReaction={(selectedEmoji) =>
            createMessageReaction('Message', message, selectedEmoji)
          }
          destroyMessageReaction={(reactionUuid) =>
            destroyMessageReaction('Message', message, reactionUuid)
          }
          destroyDialogue={{
            title: 'Delete Message',
            body: 'Delete this message and any attached media?',
          }}
        />
      </Container>
      {prevIsMoreThanHourAgo && (
        <div
          style={{
            width: '100svw',
            fontSize: size(1.5),
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: colors.statusColors.right_now('light'),
            padding: `${size(0.5)} 0 ${size(1.7)} 0`,
          }}
        >
          {renderLargeTimeGap()}
        </div>
      )}
    </>
  )
}

export default Message
